import loadConversationReadingPane from './loadConversationReadingPane';
import moveReadingPaneToTab from './moveReadingPaneToTab';
import ConversationReadingPaneViewState from '../store/schema/ConversationReadingPaneViewState';
import readingPaneStore from '../store/Store';
import { ClientItemId } from 'owa-client-ids';
import { getFolderIdForSelectedNode } from 'owa-mail-folder-forest-store';
import { mailStore, InstrumentationContext } from 'owa-mail-store';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getActiveContentTab, TabType } from 'owa-tab-store';
import { action, select } from 'satcheljs/lib/legacy';
import { AsyncSendState, ComposeViewState } from 'owa-mail-compose-store';
import {
    findComposeFromTab,
    lazyMoveComposeToTab,
    lazyTrySaveAndCloseCompose,
} from 'owa-mail-compose-actions';

export interface LoadConversationReadingPaneForSingleMailItemSelectedState {
    conversationReadingPaneState: ConversationReadingPaneViewState;
    hasConversationInMailStore: boolean;
}

export default action('loadConversationReadingPaneForSingleMailItemSelected')(
    select<LoadConversationReadingPaneForSingleMailItemSelectedState>({
        conversationReadingPaneState: () => readingPaneStore.conversationReadingPaneViewState,
        hasConversationInMailStore: (conversationId: ClientItemId) =>
            mailStore.conversations.has(conversationId.Id),
    })(async function loadConversationReadingPaneForSingleMailItemSelected(
        conversationId: ClientItemId,
        isUserNavigation: boolean,
        instrumentationContext: InstrumentationContext,
        conversationSubject: string,
        conversationCategories: string[],
        itemToScrollTo?: string,
        state?: LoadConversationReadingPaneForSingleMailItemSelectedState
    ): Promise<void> {
        const activeTab = getActiveContentTab();
        let composeViewState: ComposeViewState;

        composeViewState = state.conversationReadingPaneState
            ? findComposeFromTab(
                  activeTab,
                  state.conversationReadingPaneState.conversationId.Id,
                  ReactListViewType.Conversation
              )
            : null;

        const isInlineCompose = composeViewState ? composeViewState.isInlineCompose : false;

        if (
            (state.conversationReadingPaneState &&
                state.conversationReadingPaneState.conversationId.Id == conversationId.Id &&
                state.conversationReadingPaneState.currentSelectedFolderId ==
                    getFolderIdForSelectedNode() &&
                state.hasConversationInMailStore) ||
            (composeViewState &&
                composeViewState.asyncSendState !== AsyncSendState.Delay &&
                !isUserNavigation)
        ) {
            // Skip in two cases:
            // 1. loading the same conversation in the same folder
            // 2. user has not explicitly asked (!isUserNavigation) to navigate to a different conversation
            return;
        }

        if (composeViewState) {
            // Properly handle compose before loading new conversation to reading pane store
            if (activeTab) {
                // SecondaryReadingPane and Primary tabs w/ inline compose should move the whole RP.
                if (activeTab.type == TabType.SecondaryReadingPane || isInlineCompose) {
                    // In the delay send case, we want to move the reading pane to a tab in case
                    // we need to bring it back if the user hits Undo. Since it should appear the
                    // message has been sent, hide this tab for now and show it if Undo is activated
                    const makeHidden = composeViewState.asyncSendState == AsyncSendState.Delay;
                    moveReadingPaneToTab(
                        state.conversationReadingPaneState.conversationId,
                        state.conversationReadingPaneState.conversationSubject,
                        state.conversationReadingPaneState.conversationCategories,
                        false /* makeActive */,
                        instrumentationContext,
                        ReactListViewType.Conversation /* listViewType */,
                        makeHidden
                    );
                } else {
                    // In the drafts folder, full compose opens in Primary tab.
                    lazyMoveComposeToTab.importAndExecute(composeViewState, true, false);
                }
                return loadConversationReadingPane(
                    conversationId,
                    instrumentationContext,
                    conversationSubject,
                    conversationCategories,
                    itemToScrollTo
                );
            } else {
                if (composeViewState.isInlineCompose) {
                    lazyTrySaveAndCloseCompose.importAndExecute(composeViewState).then(async () => {
                        if (!composeViewState) {
                            return loadConversationReadingPane(
                                conversationId,
                                instrumentationContext,
                                conversationSubject,
                                conversationCategories,
                                itemToScrollTo
                            );
                        }
                    });
                } else {
                    // Only when the full compose view state in ComposeStore is set as null, the reading pane container
                    // will be replaced with the conversation content.
                    // If full compose has save failure, the defaultViewState in ComposeStore will not be set as null,
                    // so the reading pane container still keeps the current full compose form even if it triggers
                    // loadConversationReadingPane action.
                    lazyTrySaveAndCloseCompose.importAndExecute(composeViewState);

                    return loadConversationReadingPane(
                        conversationId,
                        instrumentationContext,
                        conversationSubject,
                        conversationCategories,
                        itemToScrollTo
                    );
                }
            }
        } else {
            return loadConversationReadingPane(
                conversationId,
                instrumentationContext,
                conversationSubject,
                conversationCategories,
                itemToScrollTo
            );
        }
    })
);
